import React from 'react';
import { StageSection, Prose, KeyPoints } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Consensus & the Security Model',
    tagline: 'What Starknet trusts today, and where its bugs actually live.',
};

// This stage expects 2 quiz questions configured in the admin panel, in this order:
//   Q0 - what decentralizing the sequencer/prover roles is meant to remove
//   Q1 - where, in practice, Starknet security failures tend to come from
export const blocks: StageBlock[] = [
    content('hero', () => (
        <StageSection gradientBorder eyebrow="Stage 8 of 8" title="Today's trust assumptions, and tomorrow's">
            <Prose>
                Earlier phases of Starknet ran with a single, Starkware-operated sequencer and prover. The validity
                proof still guarantees that whatever was executed followed the rules, but a single sequencer can
                unilaterally decide transaction ordering (censoring or reordering transactions before they're ever
                proven) and can halt the network by simply stopping. Starknet's decentralization roadmap replaces
                that single operator with a permissionless, staked set of sequencers and provers under a
                Byzantine-fault-tolerant consensus protocol, so no single party controls ordering or liveness.
            </Prose>
        </StageSection>
    )),

    question(0),

    content('what-doesnt-change', () => (
        <StageSection title="What decentralizing the sequencer does not change">
            <Prose>
                None of that affects the guarantee this track has centered on: a validity proof only ever attests
                to what the Cairo code actually did. Decentralizing who runs the sequencer and prover removes a
                trust assumption about liveness and censorship; it does nothing to make a poorly written contract
                safer. That responsibility sits entirely with whoever wrote the Cairo code.
            </Prose>
        </StageSection>
    )),

    content('where-bugs-live', () => (
        <StageSection title="Where Starknet security failures actually come from">
            <Prose>
                Across the pieces this track has covered, the recurring theme is that Starknet moves several things
                Ethereum handles at the protocol level into contract-level code instead: authorization
                (<code>__validate__</code>), fee handling, and upgrade logic all become surfaces a contract author
                has to get right themselves.
            </Prose>
            <KeyPoints items={[
                <>Missing or bypassable caller checks in account or contract entrypoints.</>,
                <>Field-arithmetic assumptions borrowed from bounded-integer languages that silently wrap instead of reverting.</>,
                <>Replayable signatures or validation paths in account contracts.</>,
                <>Unsafe upgrade and deployment flows around declared classes.</>,
                <>Cross-contract and cross-layer calls that assume synchronous, atomic behavior they don't actually have.</>,
            ]} />
        </StageSection>
    )),

    question(1),

    content('next-steps', () => (
        <StageSection gradientBorder title="Where the Cairo track goes from here">
            <Prose>
                The Foundation stages ahead rebuild Cairo itself (values, ownership, structs, traits, and tests),
                then map those rules onto real Starknet contracts. From there, the security stages exploit exactly
                the failure modes above: caller authorization, field-arithmetic accounting, state and lifecycle
                assumptions, untrusted cross-contract calls, signature replay, denial of service, and unsafe
                upgrades. Everything in this Network Basics track is the mental model those challenges assume you
                already have.
            </Prose>
        </StageSection>
    )),
];
